import React, { useState } from 'react'
import { Settings } from 'lucide-react'
import { SettingsTabs, DEFAULT_TABS } from '../components/SettingsTabs'
import MarketplaceSettingsPanel from '../components/MarketplaceSettingsPanel'
import EnterpriseSettingsPanel from '../components/EnterpriseSettingsPanel'
import GovernmentSettingsPanel from '../components/GovernmentSettingsPanel'
import AccountSettingsPanel from '../components/AccountSettingsPanel'
import CaptchaStatisticsPanel from '../components/CaptchaStatisticsPanel'

/**
 * Admin Settings Page (Module A - Scaffolding)
 * 
 * This is the main entry point for the admin settings UI.
 * It provides a tabbed interface for managing different system modules.
 * 
 * Following OCP (Open/Closed Principle):
 * - This is a NEW component that doesn't modify existing admin code
 * - Settings panels are loaded dynamically based on active tab
 * - Each module (Marketplace, Enterprise, Government) is self-contained
 */
const AdminSettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('marketplace')

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    console.log(`📑 Switched to ${tabId} settings`)
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8 pb-safe">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
            <Settings className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Admin Settings
            </h1>
          </div>
          <p className="text-gray-600 ml-8 sm:ml-11 text-sm sm:text-base">
            Comprehensive system configuration for Marketplace, Enterprise, and Government modules
          </p>
        </div>

        {/* Tabs Navigation */}
        <SettingsTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={DEFAULT_TABS}
        />

        {/* Tab Panels */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
          {/* Marketplace Panel */}
          {activeTab === 'marketplace' && (
            <div role="tabpanel" id="marketplace-panel" aria-labelledby="marketplace-tab">
              <MarketplaceSettingsPanel />
            </div>
          )}

          {/* Enterprise Panel */}
          {activeTab === 'enterprise' && (
            <div role="tabpanel" id="enterprise-panel" aria-labelledby="enterprise-tab">
              <EnterpriseSettingsPanel />
            </div>
          )}

          {/* Government Panel */}
          {activeTab === 'government' && (
            <div role="tabpanel" id="government-panel" aria-labelledby="government-tab">
              <GovernmentSettingsPanel />
            </div>
          )}

          {/* Account Settings Panel */}
          {activeTab === 'account' && (
            <div role="tabpanel" id="account-panel" aria-labelledby="account-tab">
              <AccountSettingsPanel />
            </div>
          )}

          {/* CAPTCHA Statistics Panel */}
          {activeTab === 'captcha' && (
            <div role="tabpanel" id="captcha-panel" aria-labelledby="captcha-tab">
              <CaptchaStatisticsPanel />
            </div>
          )}
        </div>

        {/* Module Info Footer */}
        <div className="mt-6 p-4 bg-gray-100 rounded-lg border border-gray-300">
          <p className="text-sm text-gray-700">
            <strong>📦 Module A - Scaffolding Complete:</strong> Tab navigation, routing, and mock API layer implemented.
            Each module (B-F) will be built progressively with approval checkpoints.
          </p>
        </div>
      </div>
    </div>
  )
}

export default AdminSettingsPage

